// Phase 10A.1 R0/R1 — Knowledge page mock-data purge.
//
// Removed in this slice:
//   - MOCK_PATHS array (fictional learning paths with invented
//     lessons + progress percentages).
//   - MOCK fallback. The page now reads useKnowledgePaths against
//     the seeded knowledge_paths + knowledge_lessons tables
//     (migrations 0086, 0087). When seeded data exists it renders;
//     when none yet, truthful empty state.

import { Icon } from "../../components/icons/Icon.tsx";
import { useKnowledgePaths } from "../../knowledge/useKnowledgePaths.ts";
import { AwaitingDataPanel } from "../_awaiting-data.tsx";

export function KnowledgePage(): JSX.Element {
  const { paths, loading, error } = useKnowledgePaths();

  return (
    <>
      <div className="ex-head">
        <div>
          <h1>Knowledge</h1>
          <div className="sub">
            Learning paths covering the doctrine, the agent pipeline, and the discipline behind
            STRIKZA. Read-only educational content, not betting advice.
          </div>
        </div>
        <div className="pill-row">
          <span className="tag">Educational</span>
        </div>
      </div>

      <div className="disclosure">
        <Icon name="info" size={14} />
        <span>
          Knowledge content is general education. Nothing here recommends a stake, a placement, or a
          specific outcome. Manual placement only.
        </span>
      </div>

      {loading ? (
        <AwaitingDataPanel
          eyebrow="Knowledge"
          title="Loading…"
          body="Authenticated read pending."
        />
      ) : error !== null ? (
        <AwaitingDataPanel eyebrow="Knowledge" title="Knowledge unavailable." body={error} />
      ) : paths.length === 0 ? (
        <AwaitingDataPanel
          eyebrow="Knowledge"
          title="No paths available yet."
          body={
            <>
              Knowledge paths come from the seeded knowledge_paths + knowledge_lessons tables. If
              you&rsquo;re seeing this, the seed has not yet run for your environment.
            </>
          }
        />
      ) : (
        <div
          style={{
            marginTop: 24,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {paths.map((p) => (
            <div
              key={p.id}
              style={{
                padding: 20,
                border: "1px solid var(--border-faint, #2a2a2a)",
                borderRadius: 8,
                background: "var(--bg-elev-1, transparent)",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-head, inherit)",
                  fontSize: 16,
                  color: "var(--text-primary, inherit)",
                  lineHeight: 1.3,
                }}
              >
                {p.title}
              </div>
              <p
                style={{
                  fontFamily: "var(--font-body, inherit)",
                  fontSize: 13,
                  color: "var(--text-secondary, inherit)",
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                {p.description ?? ""}
              </p>
              <span
                style={{
                  fontFamily: "var(--font-mono, monospace)",
                  fontSize: 11,
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  color: "var(--text-tertiary, #666)",
                }}
              >
                {p.completedCount} / {p.lessons.length} completed
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
